import { useRef } from 'react';
import { cn } from '../../lib/utils';

/**
 * 3D tilt + cursor spotlight + glare. Writes CSS vars on pointermove (no React
 * re-render). Wrap any element. Disabled under prefers-reduced-motion.
 */
export default function Tilt({
  children,
  className,
  max = 12,
  scale = 1.02,
  glare = true,
  spotlight = true,
  ...props
}) {
  const ref = useRef(null);
  const reduce =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const onMove = (e) => {
    if (reduce) return;
    const el = ref.current;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    el.style.setProperty('--rx', `${((0.5 - py) * max * 2).toFixed(2)}deg`);
    el.style.setProperty('--ry', `${((px - 0.5) * max * 2).toFixed(2)}deg`);
    el.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`);
    el.style.setProperty('--my', `${(py * 100).toFixed(1)}%`);
    el.style.setProperty('--tilt-scale', String(scale));
  };
  const onLeave = () => {
    const el = ref.current;
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
    el.style.setProperty('--tilt-scale', '1');
  };

  return (
    <div
      ref={ref}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      className={cn('relative', spotlight && 'spotlight', className)}
      style={{
        transform:
          'perspective(900px) rotateX(var(--rx,0deg)) rotateY(var(--ry,0deg)) scale(var(--tilt-scale,1))',
        transition: 'transform .3s cubic-bezier(0.16,1,0.3,1)',
        transformStyle: 'preserve-3d',
        willChange: 'transform',
      }}
      {...props}
    >
      {children}
      {glare && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-300 hover:opacity-100"
          style={{
            background:
              'radial-gradient(500px circle at var(--mx,50%) var(--my,50%), rgba(255,255,255,0.10), transparent 45%)',
          }}
        />
      )}
    </div>
  );
}
